import { ScreenSizeStore } from '@Components/ScreenSize/ScreenSizeStore';
import { ObservableProp, useClassStore } from '@Utils/useClassStore';
import { inject, injectable } from 'inversify';
import { computed } from 'mobx';

import container from '~ioc/ioc';

type ExtendedMapLayoutModes = 'compact' | 'full' | 'mobile';

const BREAKPOINTS = [1100, 720];
const TITLE_HEIGHT = 50;
const TITLE_HEIGHT_MOBILE = 28;

export const OTHER_PAGES_SPACE_HEIGHT = 55;
const MAP_PAGES_MAX_HEIGHT = 460 + OTHER_PAGES_SPACE_HEIGHT;
export const MAP_TITLE_HEIGHT_WITH_PADDING = 69 + 15;
export const MAP_PAGES_MAX_WIDTH = 741;
const ELEVATOR_PANE_MIN_WIDTH = 160;
export const ELEVATOR_PANE_LEFT_PADDING = 30;

@injectable()
class FloorMapSizer {
  pagesContainer = new ObservableProp<{ width: number; height: number }>({
    width: 0,
    height: 0,
  });

  @inject(ScreenSizeStore) private _screenSizeStore!: ScreenSizeStore;

  @computed get windowWidth(): number {
    return this._screenSizeStore.window.value.width;
  }

  @computed get windowHeight(): number {
    return this._screenSizeStore.window.value.height;
  }

  @computed get screenOrientation(): 'landscape' | 'portrait' {
    return this.windowHeight > this.windowWidth ? 'portrait' : 'landscape';
  }

  @computed get mapOrientation(): 'landscape' | 'portrait' {
    if (this.screenOrientation === 'landscape') {
      return 'landscape';
    }
    if (this.windowWidth > 700) {
      return 'landscape';
    }
    return 'portrait';
  }

  @computed get layoutMode(): ExtendedMapLayoutModes {
    const [FULL, COMPACT] = BREAKPOINTS;
    if (this.windowWidth >= FULL) {
      return 'full';
    }

    if (this.windowWidth >= COMPACT) {
      return 'compact';
    }

    return 'mobile';
  }

  @computed get titleHeight(): number {
    return this.layoutMode === 'mobile' ? TITLE_HEIGHT_MOBILE : TITLE_HEIGHT;
  }

  @computed get pagesMaxHeightLandscape(): number {
    return this.windowHeight >
      MAP_PAGES_MAX_HEIGHT + MAP_TITLE_HEIGHT_WITH_PADDING
      ? MAP_PAGES_MAX_HEIGHT
      : this.windowHeight - MAP_TITLE_HEIGHT_WITH_PADDING;
  }

  @computed get justifyContentLandscape(): 'center' | 'flex-end' {
    return this.windowHeight >
      MAP_PAGES_MAX_HEIGHT + MAP_TITLE_HEIGHT_WITH_PADDING * 2
      ? 'center'
      : 'flex-end';
  }

  @computed get marginBottomLandscape(): number {
    if (this.justifyContentLandscape === 'center') {
      return 0;
    }

    if (
      this.windowHeight <
      MAP_PAGES_MAX_HEIGHT + MAP_TITLE_HEIGHT_WITH_PADDING + 10
    ) {
      return 0;
    }

    return 10;
  }

  @computed get isHorizontalWideModeLandscape(): boolean {
    return this.windowWidth > MAP_PAGES_MAX_WIDTH + ELEVATOR_PANE_MIN_WIDTH * 2;
  }

  @computed get pagesMaxWidthLandscape(): number {
    return this.isHorizontalWideModeLandscape
      ? MAP_PAGES_MAX_WIDTH
      : this.windowWidth - ELEVATOR_PANE_MIN_WIDTH;
  }

  @computed get alignItemsLandscape(): 'center' | 'flex-start' {
    return this.isHorizontalWideModeLandscape ? 'center' : 'flex-start';
  }

  @computed get elevatorWidthLandscape(): number {
    return this.isHorizontalWideModeLandscape
      ? (this.windowWidth - this.pagesContainer.value.width) / 2 - 10
      : this.windowWidth - this.pagesContainer.value.width;
  }

  @computed get titleMarginLeftLandscape(): number {
    // make sure title does not fall onto ownable button
    return !this.isHorizontalWideModeLandscape && this.windowHeight < 1040
      ? 80
      : 0;
  }

  @computed get departmentFontSize(): number {
    return this.mapOrientation === 'landscape' ? 15 : 12;
  }
}

export const useFloorMapSizer = () =>
  useClassStore<FloorMapSizer>(container.get(FloorMapSizer));
